import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import FaceRetouchingOffIcon from "@mui/icons-material/FaceRetouchingOff";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HandymanOutlinedIcon from "@mui/icons-material/HandymanOutlined";
import PunchClockOutlinedIcon from "@mui/icons-material/PunchClockOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { selectUser } from "../../features/userSlice";
import { selectRoles } from "../../features/roleSlice";
import { IconTile, PageHeader, PageShell } from "../../components/UI/Page";
import {
  EmptyState,
  LoadingState,
  StatTile,
} from "../../components/UI/States";
import { formatMinutes, isJobKey, jobText } from "./pcwApi";
import { SHOP_CLOCKS, ShopClockProvider, useShopClock } from "./shopClocks";
import {
  AllottedBar,
  CsvButton,
  CustomJobChip,
  DATE_INPUT,
  HeadRow,
  JobCell,
  LoadingRow,
  MessageRow,
  SEGMENT_TRACK,
  SectionCard,
  SortableTh,
  allottedRemaining,
  csvHours,
  errorBanner,
  fileSlug,
  segmentClass,
} from "./shopClockUi";
import {
  DueChip,
  DueDateDialog,
  JobScheduleCard,
  ScheduleTab,
  todayDay,
} from "./ShopSchedule";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";

const TABS = [
  { key: "hours", label: "Hours" },
  { key: "open", label: "Open Jobs" },
  { key: "schedule", label: "Schedule" },
  { key: "punches", label: "Punch Log" },
  { key: "jobs", label: "Custom Jobs" },
  { key: "workers", label: "Workers" },
  { key: "categories", label: "Categories" },
];

// Preset ranges include today and reach back N calendar days; boundaries are
// built in the admin's local time so "Today" means their today.
const RANGES = [
  { key: "today", label: "Today", days: 1 },
  { key: "7d", label: "Last 7 Days", days: 7 },
  { key: "30d", label: "Last 30 Days", days: 30 },
  { key: "all", label: "All Time", days: null },
];

const startOfDaysAgo = (days) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - (days - 1));
  return date;
};

const dateTime = (value) =>
  value
    ? new Date(value).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const timeOnly = (value) =>
  value
    ? new Date(value).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

// Date -> value for <input type="datetime-local"> in the admin's local time.
const toLocalInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const punchMinutes = (punch) =>
  punch.status !== "closed"
    ? (Date.now() - new Date(punch.punchInAt).getTime()) / 60000
    : (new Date(punch.punchOutAt) - new Date(punch.punchInAt)) / 60000;

const ActiveBadge = ({ active, inactiveLabel = "Inactive" }) => (
  <span
    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${
      active
        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
        : "bg-slate-500/15 text-slate-600 dark:text-slate-300"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-slate-400"}`}
    />
    {active ? "Active" : inactiveLabel}
  </span>
);

// The tablet stopped asking for a category and stores "None" — shown as a
// dash like a punch with no category at all.
const categoryName = (category) =>
  category?.name && category.id !== "none" ? category.name : "—";

// ---- Allotted time ----

// Set, change, or clear a job's allotted time (hours + minutes).
const AllottedTimeDialog = ({ open, job, usedMinutes, onSave, onClose, saving, error }) => {
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");

  // Keyed on the value — callers pass a fresh `job` object every render,
  // which would otherwise reset the fields on each keystroke.
  const current = job?.allottedMinutes || 0;
  useEffect(() => {
    if (!open) return;
    setHours(current ? String(Math.floor(current / 60)) : "");
    setMinutes(current % 60 ? String(current % 60) : "");
  }, [open, current]);

  const total =
    (parseInt(hours || "0", 10) || 0) * 60 + (parseInt(minutes || "0", 10) || 0);
  const valid = total >= 1 && total <= 10000 * 60;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Allotted Time{job?.label ? ` — ${job.label}` : ""}</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-2">
          {error && errorBanner(error)}
          <p className="app-muted text-sm">
            How long this job should take. The shop floor TV's Job Hours screen
            shows the time used against it — amber at 90%, red once it's over.
          </p>
          {usedMinutes != null && (
            <p className="text-sm">
              Used so far:{" "}
              <span className="font-semibold tabular-nums">
                {formatMinutes(usedMinutes)}
              </span>
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <TextField
              size="small"
              label="Hours"
              inputProps={{ inputMode: "numeric" }}
              value={hours}
              onChange={(e) => setHours(e.target.value.replace(/\D/g, "").slice(0, 5))}
            />
            <TextField
              size="small"
              label="Minutes"
              inputProps={{ inputMode: "numeric" }}
              value={minutes}
              onChange={(e) =>
                setMinutes(
                  String(Math.min(59, parseInt(e.target.value.replace(/\D/g, "") || "0", 10)) || "")
                )
              }
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        {job?.allottedMinutes > 0 && (
          <Button color="error" disabled={saving} onClick={() => onSave(null)}>
            Clear
          </Button>
        )}
        <Button
          variant="contained"
          disabled={!valid || saving}
          onClick={() => onSave(total)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---- Punch add/edit dialog ----

// One dialog for both fixing a punch and adding a manual one. A manual punch
// is always complete (in and out); editing an open punch may leave punch out
// blank to keep the worker punched in. fixedJob ({ key, label }) pins a new
// punch to the job being viewed; otherwise it's a work order number or one
// of the custom jobs. Category is optional — the tablet itself no longer
// asks for one, so "None" is the default.
const PunchDialog = ({
  open,
  punch,
  fixedJob,
  workers,
  categories,
  customJobs,
  onSave,
  onClose,
  saving,
  error,
}) => {
  const editing = Boolean(punch?._id);
  const [workerId, setWorkerId] = useState("");
  const [categoryId, setCategoryId] = useState("none");
  const [jobType, setJobType] = useState("workOrder");
  const [workOrder, setWorkOrder] = useState("");
  const [customJobId, setCustomJobId] = useState("");
  const [inAt, setInAt] = useState("");
  const [outAt, setOutAt] = useState("");

  useEffect(() => {
    if (!open) return;
    setWorkerId(punch?.workerId || "");
    setCategoryId(punch?.category?.id || "none");
    setJobType("workOrder");
    setWorkOrder("");
    setCustomJobId("");
    setInAt(toLocalInput(punch?.punchInAt));
    setOutAt(toLocalInput(punch?.punchOutAt));
  }, [open, punch]);

  const openPunch = editing && punch.status !== "closed";
  const jobKey =
    fixedJob?.key ||
    (jobType === "custom"
      ? customJobId && `c-${customJobId}`
      : workOrder);
  const canSave =
    workerId &&
    (editing || jobKey) &&
    inAt &&
    (outAt || openPunch) &&
    !saving;

  const save = () => {
    const fields = {
      workerId,
      categoryId,
      punchInAt: new Date(inAt).toISOString(),
    };
    if (outAt) fields.punchOutAt = new Date(outAt).toISOString();
    if (!editing) {
      if (jobKey.startsWith("c-")) fields.customWorkOrderId = jobKey.slice(2);
      else fields.worksOrderNumber = parseInt(jobKey, 10);
    }
    onSave(fields);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {editing
          ? `Fix Punch — ${punch.workerName}`
          : `Add Manual Punch${fixedJob ? ` — ${fixedJob.label}` : ""}`}
      </DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-2">
          {error && errorBanner(error)}
          <FormControl fullWidth size="small">
            <InputLabel>Worker</InputLabel>
            <Select
              label="Worker"
              value={workerId}
              onChange={(e) => setWorkerId(e.target.value)}
            >
              {(workers || []).map((worker) => (
                <MenuItem key={worker._id} value={String(worker._id)}>
                  {worker.name}
                  {!worker.active && " (inactive)"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!editing && !fixedJob && (
            <div className="space-y-2">
              <div role="group" aria-label="Job type" className={SEGMENT_TRACK}>
                {[
                  { key: "workOrder", label: "Work Order #" },
                  { key: "custom", label: "Custom Job" },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    aria-pressed={jobType === option.key}
                    onClick={() => setJobType(option.key)}
                    className={segmentClass(jobType === option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {jobType === "workOrder" ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Work order #"
                  value={workOrder}
                  onChange={(e) =>
                    setWorkOrder(e.target.value.replace(/\D/g, ""))
                  }
                />
              ) : (customJobs || []).length === 0 ? (
                <p className="app-muted text-xs">
                  No custom jobs yet — workers create them on the shop tablet.
                </p>
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>Custom job</InputLabel>
                  <Select
                    label="Custom job"
                    value={customJobId}
                    onChange={(e) => setCustomJobId(e.target.value)}
                  >
                    {customJobs.map((job) => (
                      <MenuItem key={job._id} value={String(job._id)}>
                        {job.name}
                        {!job.active && " (finished)"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </div>
          )}
          <FormControl fullWidth size="small">
            <InputLabel>Category (optional)</InputLabel>
            <Select
              label="Category (optional)"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <MenuItem value="none">None</MenuItem>
              {(categories || []).map((category) => (
                <MenuItem key={category._id} value={String(category._id)}>
                  {category.name}
                  {!category.active && " (inactive)"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div className="grid grid-cols-1 gap-4">
            <label className="block">
              <span className="app-muted text-xs font-medium">
                Punched In
              </span>
              <input
                type="datetime-local"
                className={`${DATE_INPUT} mt-1 w-full`}
                value={inAt}
                onChange={(e) => setInAt(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="app-muted text-xs font-medium">
                Punched Out
              </span>
              <input
                type="datetime-local"
                className={`${DATE_INPUT} mt-1 w-full`}
                value={outAt}
                onChange={(e) => setOutAt(e.target.value)}
              />
              {openPunch && !outAt && (
                <span className="app-muted mt-1 block text-xs">
                  Leave blank to keep them punched in.
                </span>
              )}
            </label>
          </div>
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={save}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---- Shared punch table ----

const PUNCH_CSV_HEADERS = [
  { label: "Worker", key: "worker" },
  { label: "Work Order", key: "workOrder" },
  { label: "Job", key: "job" },
  { label: "Custom Job", key: "customJob" },
  { label: "Order Number", key: "orderNumber" },
  { label: "Customer", key: "customer" },
  { label: "Category", key: "category" },
  { label: "Punched In", key: "punchedIn" },
  { label: "Punched Out", key: "punchedOut" },
  { label: "Minutes", key: "minutes" },
  { label: "Hours", key: "hours" },
  { label: "Status", key: "status" },
  { label: "Closed By", key: "closedBy" },
  { label: "Manual Entry", key: "manual" },
  { label: "Edited By Admin", key: "edited" },
];

const punchesToCsv = (punches) =>
  (punches || []).map((punch) => {
    const minutes = Math.round(punchMinutes(punch));
    return {
      worker: punch.workerName,
      workOrder: punch.workOrder?.worksOrderNumber ?? "",
      job: punch.workOrder?.description ?? "",
      customJob: punch.workOrder?.custom ? "yes" : "",
      orderNumber: punch.workOrder?.orderNumber ?? "",
      customer: punch.workOrder?.customerName ?? "",
      category: punch.category?.name ?? "",
      punchedIn: punch.punchInAt ? new Date(punch.punchInAt).toLocaleString() : "",
      punchedOut: punch.punchOutAt
        ? new Date(punch.punchOutAt).toLocaleString()
        : "",
      minutes,
      hours: csvHours(minutes),
      status: punch.status === "closed" ? "closed" : "open",
      closedBy: punch.closedBy || "",
      manual: punch.manual ? "yes" : "",
      edited: punch.editedByAdmin?.name || "",
    };
  });

const PunchRows = ({
  punches,
  showWorkOrder,
  finishedJobIds,
  onEdit,
  onForceClose,
  closingId,
  colSpan,
}) => {
  if (punches === null)
    return <LoadingRow colSpan={colSpan}>Loading punches...</LoadingRow>;
  if (punches.length === 0)
    return (
      <MessageRow colSpan={colSpan}>
        No punches match the current range and filters.
      </MessageRow>
    );
  return punches.map((punch) => {
    const open = punch.status !== "closed";
    return (
      <tr key={punch._id}>
        <td className="whitespace-nowrap px-4 py-3 font-medium">
          {punch.workerName}
        </td>
        {showWorkOrder && (
          <td className="max-w-[240px] px-4 py-3">
            <JobCell
              workOrder={punch.workOrder}
              finished={finishedJobIds?.has(punch.workOrder?.customWorkOrderId)}
            />
            {!punch.workOrder?.custom && punch.workOrder?.customerName && (
              <div className="app-muted truncate text-xs">
                {punch.workOrder.customerName}
              </div>
            )}
          </td>
        )}
        <td className="whitespace-nowrap px-4 py-3">
          {categoryName(punch.category)}
        </td>
        <td className="whitespace-nowrap px-4 py-3 tabular-nums">
          {dateTime(punch.punchInAt)}
        </td>
        <td className="whitespace-nowrap px-4 py-3 tabular-nums">
          {open ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
              Still punched in
            </span>
          ) : (
            <>
              {timeOnly(punch.punchOutAt)}
              {(punch.manual ||
                punch.closedBy === "admin" ||
                punch.closedBy === "payroll" ||
                punch.editedByAdmin) && (
                <span
                  className="app-muted ml-1 text-xs"
                  title={
                    !punch.manual && !punch.editedByAdmin && punch.closedBy === "payroll"
                      ? "Left punched in — ended at the time payroll shows they left work"
                      : undefined
                  }
                >
                  ({punch.manual
                    ? "manual"
                    : punch.editedByAdmin
                    ? "edited"
                    : punch.closedBy === "payroll"
                    ? "payroll"
                    : "by admin"})
                </span>
              )}
            </>
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-3 font-semibold tabular-nums">
          {formatMinutes(punchMinutes(punch))}
        </td>
        <td className="whitespace-nowrap px-4 py-2 text-right">
          <Button
            size="small"
            startIcon={<EditOutlinedIcon />}
            onClick={() => onEdit(punch)}
          >
            Fix
          </Button>
          {open && (
            <Button
              size="small"
              color="warning"
              disabled={closingId === punch._id}
              onClick={() => onForceClose(punch)}
            >
              Force Punch Out
            </Button>
          )}
        </td>
      </tr>
    );
  });
};

// ---- Hours breakdown tables (shared by the Hours tab and the WO detail) ----

const WorkerHoursTable = ({ rows, filenameSuffix }) => {
  const { shop } = useShopClock();
  return (
    <SectionCard
      title="Hours by Worker"
      action={
        <CsvButton
          data={(rows || []).map((row) => ({
            worker: row.name,
            minutes: row.minutes,
            hours: csvHours(row.minutes),
            punches: row.punches,
            workOrders: row.workOrders,
          }))}
          headers={[
            { label: "Worker", key: "worker" },
            { label: "Minutes", key: "minutes" },
            { label: "Hours", key: "hours" },
            { label: "Punches", key: "punches" },
            { label: "Work Orders", key: "workOrders" },
          ]}
          filename={`${shop.fileSlug}-hours-by-worker${filenameSuffix}.csv`}
        />
      }
    >
      <table className="app-table">
        <HeadRow columns={["Worker", "Hours", "Punches", "Work Orders"]} />
        <tbody>
          {rows.length === 0 ? (
            <MessageRow colSpan={4}>No closed punches in this range.</MessageRow>
          ) : (
            rows.map((row) => (
              <tr key={row.workerId}>
                <td className="px-4 py-3 font-medium">{row.name}</td>
                <td className="whitespace-nowrap px-4 py-3 font-semibold tabular-nums">
                  {formatMinutes(row.minutes)}
                </td>
                <td className="px-4 py-3 tabular-nums">{row.punches}</td>
                <td className="px-4 py-3 tabular-nums">{row.workOrders}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </SectionCard>
  );
};

const CategoryHoursTable = ({ rows, filenameSuffix }) => {
  const { shop } = useShopClock();
  return (
    <SectionCard
      title="Hours by Category"
      action={
        <CsvButton
          data={(rows || []).map((row) => ({
            category: row.name,
            minutes: row.minutes,
            hours: csvHours(row.minutes),
            punches: row.punches,
            workers: (row.workers || []).join(", "),
          }))}
          headers={[
            { label: "Category", key: "category" },
            { label: "Minutes", key: "minutes" },
            { label: "Hours", key: "hours" },
            { label: "Punches", key: "punches" },
            { label: "Workers", key: "workers" },
          ]}
          filename={`${shop.fileSlug}-hours-by-category${filenameSuffix}.csv`}
        />
      }
    >
      <table className="app-table">
        <HeadRow columns={["Category", "Hours", "Punches", "Workers"]} />
        <tbody>
          {rows.length === 0 ? (
            <MessageRow colSpan={4}>No closed punches in this range.</MessageRow>
          ) : (
            rows.map((row) => (
              <tr key={String(row.categoryId)}>
                <td className="px-4 py-3 font-medium">{row.name}</td>
                <td className="whitespace-nowrap px-4 py-3 font-semibold tabular-nums">
                  {formatMinutes(row.minutes)}
                </td>
                <td className="px-4 py-3 tabular-nums">{row.punches}</td>
                <td
                  className="app-muted max-w-[200px] truncate px-4 py-3"
                  title={(row.workers || []).join(", ")}
                >
                  {(row.workers || []).join(", ")}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </SectionCard>
  );
};

// ---- Hours tab ----

// Why the evening payroll check (server payrollPunchCheck.js) couldn't end
// an open punch on its own — payrollCheck.issue.
const PAYROLL_ISSUES = {
  "missed-out": "Payroll shows they left without punching out of work",
  "out-before-punch-in": "Payroll shows they clocked out of work before this punch started",
  "out-on-later-day": "Payroll's last punch for them is on a later day",
  "unreadable-time": "Payroll's punch-out time couldn't be read",
  "still-in": "Payroll still had them clocked in to work at the end of the night",
  unknown: "UKG doesn't recognize their UKG # — check it on the Workers tab",
};

const OpenPunches = ({ punches, onClose, closingId }) => (
  <section className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 sm:p-5">
    <h3 className="flex items-center gap-2 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
      </span>
      Punched In Right Now
      <span className="rounded-full bg-emerald-500/15 px-2 text-xs tabular-nums">
        {punches.length}
      </span>
    </h3>
    <div className="mt-3 space-y-2">
      {punches.map((punch) => (
        <div
          key={punch._id}
          className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white/70 px-3 py-2 text-sm dark:bg-white/5"
        >
          <div className="min-w-0">
            <span className="font-semibold">{punch.workerName}</span>
            <span className="app-muted tabular-nums">
              {" "}
              — {jobText(punch.workOrder)} · in at {timeOnly(punch.punchInAt)}{" "}
              ({formatMinutes(punchMinutes(punch))})
            </span>
            {PAYROLL_ISSUES[punch.payrollCheck?.issue] && (
              <div className="mt-0.5 flex items-start gap-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                <WarningAmberOutlinedIcon sx={{ fontSize: 14, mt: "1px" }} />
                <span>
                  {PAYROLL_ISSUES[punch.payrollCheck.issue]}
                  {punch.payrollCheck.lastPunchLocal &&
                    punch.payrollCheck.issue !== "missed-out" &&
                    ` (${punch.payrollCheck.issue === "still-in" ? "last punch " : ""}${
                      punch.payrollCheck.lastPunchLocal
                    })`}
                  {" "}— set their punch-out time with Fix on the Punch Log.
                </span>
              </div>
            )}
          </div>
          <Button
            size="small"
            color="warning"
            disabled={closingId === punch._id}
            onClick={() => onClose(punch)}
          >
            Force Punch Out
          </Button>
        </div>
      ))}
    </div>
  </section>
);

// Sort accessors for the work order table; direction is the natural default
// for each column when it's first clicked.
// Work order sort: numbered work orders in order, then custom jobs by name.
const WO_COLUMNS = [
  {
    key: "workOrder",
    label: "Work Order",
    value: (row) =>
      row.custom
        ? `z${(row.description || "").toLowerCase()}`
        : `a${String(row.worksOrderNumber ?? "").padStart(12, "0")}`,
    defaultDir: "asc",
  },
  { key: "customer", label: "Customer", value: (row) => (row.customerName || "").toLowerCase(), defaultDir: "asc" },
  { key: "minutes", label: "Hours", value: (row) => row.minutes, defaultDir: "desc" },
  { key: "punches", label: "Punches", value: (row) => row.punches, defaultDir: "desc" },
  { key: "workers", label: "Workers", value: (row) => row.workers.length, defaultDir: "desc" },
  { key: "lastActivity", label: "Last Activity", value: (row) => (row.lastActivity ? new Date(row.lastActivity).getTime() : 0), defaultDir: "desc" },
];

const WO_PAGE_SIZES = [10, 25, 50, 100];

const HoursTab = ({
  summary,
  error,
  onForceClose,
  closingId,
  onOpenWorkOrder,
  finishedJobIds,
}) => {
  const { shop } = useShopClock();
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("lastActivity");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  if (error) return errorBanner(error);
  if (!summary) return <LoadingState label="Loading hours…" />;

  const suffix = `-${new Date().toISOString().slice(0, 10)}`;

  const query = search.trim().toLowerCase();
  const filteredWorkOrders = query
    ? summary.byWorkOrder.filter((row) =>
        [
          row.worksOrderNumber,
          row.orderNumber,
          row.description,
          row.custom ? "custom job" : "",
          row.customerName,
          row.customerCode,
          ...row.workers,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query)
      )
    : summary.byWorkOrder;

  const sortColumn = WO_COLUMNS.find((column) => column.key === sortKey);
  const sortedWorkOrders = [...filteredWorkOrders].sort((a, b) => {
    const va = sortColumn.value(a);
    const vb = sortColumn.value(b);
    if (va === vb) return 0;
    return (va < vb ? -1 : 1) * (sortDir === "asc" ? 1 : -1);
  });

  const pageCount = Math.max(1, Math.ceil(sortedWorkOrders.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = sortedWorkOrders.slice(
    safePage * pageSize,
    safePage * pageSize + pageSize
  );

  const onSort = (key) => {
    if (key === sortKey) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(WO_COLUMNS.find((column) => column.key === key).defaultDir);
    }
    setPage(0);
  };

  return (
    <div className="space-y-6">
      {summary.openPunches.length > 0 && (
        <OpenPunches
          punches={summary.openPunches}
          onClose={onForceClose}
          closingId={closingId}
        />
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          icon={AccessTimeIcon}
          tone="sky"
          value={formatMinutes(summary.totals.minutes)}
          label="Hours Worked"
        />
        <StatTile
          icon={PunchClockOutlinedIcon}
          value={summary.totals.punches}
          label="Punches"
        />
        <StatTile
          icon={ReceiptLongOutlinedIcon}
          value={summary.totals.workOrders}
          label="Work Orders"
        />
        <StatTile
          icon={GroupsOutlinedIcon}
          value={summary.totals.workers}
          label="Workers"
        />
      </div>

      <SectionCard
        title="Hours by Work Order"
        subtitle="Closed punches in the selected range, including custom jobs created on the shop tablet — click a row for the full breakdown and manual punches."
        action={
          <>
            <TextField
              size="small"
              placeholder="Search WO #, job, customer, worker..."
              inputProps={{ "aria-label": "Search work orders" }}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="w-full sm:w-64"
            />
            <CsvButton
              data={sortedWorkOrders.map((row) => ({
              workOrder: row.worksOrderNumber ?? "",
              job: row.description,
              customJob: row.custom ? "yes" : "",
              orderNumber: row.orderNumber ?? "",
              customer: row.customerName,
              customerCode: row.customerCode,
              minutes: row.minutes,
              hours: csvHours(row.minutes),
              punches: row.punches,
              workers: row.workers.join(", "),
              lastActivity: row.lastActivity
                ? new Date(row.lastActivity).toLocaleString()
                : "",
              }))}
              headers={[
                { label: "Work Order", key: "workOrder" },
                { label: "Job", key: "job" },
                { label: "Custom Job", key: "customJob" },
                { label: "Order Number", key: "orderNumber" },
                { label: "Customer", key: "customer" },
                { label: "Customer Code", key: "customerCode" },
                { label: "Minutes", key: "minutes" },
                { label: "Hours", key: "hours" },
                { label: "Punches", key: "punches" },
                { label: "Workers", key: "workers" },
                { label: "Last Activity", key: "lastActivity" },
              ]}
              filename={`${shop.fileSlug}-hours-by-workorder${suffix}.csv`}
            />
          </>
        }
        footer={
          sortedWorkOrders.length > 0 && (
            <div className="app-divider flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2.5 text-sm sm:px-5">
              <span className="app-muted tabular-nums">
                Showing {safePage * pageSize + 1}–
                {Math.min((safePage + 1) * pageSize, sortedWorkOrders.length)} of{" "}
                {sortedWorkOrders.length} work orders
              </span>
              <div className="flex items-center gap-2">
                <FormControl size="small">
                  <Select
                    value={pageSize}
                    inputProps={{ "aria-label": "Rows per page" }}
                    onChange={(e) => {
                      setPageSize(e.target.value);
                      setPage(0);
                    }}
                  >
                    {WO_PAGE_SIZES.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size} / page
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  disabled={safePage === 0}
                  onClick={() => setPage(safePage - 1)}
                >
                  Prev
                </Button>
                <span className="app-muted whitespace-nowrap tabular-nums">
                  {safePage + 1} / {pageCount}
                </span>
                <Button
                  size="small"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage(safePage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )
        }
      >
        <table className="app-table">
          <thead>
            <tr>
              {WO_COLUMNS.map((column) => (
                <SortableTh
                  key={column.key}
                  label={column.label}
                  column={column.key}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={onSort}
                />
              ))}
              <th className="px-4">
                <span className="sr-only">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <MessageRow colSpan={7}>
                {summary.byWorkOrder.length === 0
                  ? "No closed punches in this range."
                  : "No work orders match your search."}
              </MessageRow>
            ) : (
              pageRows.map((row) => (
                <tr
                  key={String(row.key)}
                  onClick={() => row.key && onOpenWorkOrder(row.key)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && row.key && onOpenWorkOrder(row.key)
                  }
                  tabIndex={0}
                  className="group cursor-pointer focus-visible:bg-[color:var(--app-hover)]"
                >
                  <td className="max-w-[240px] px-4 py-3">
                    <JobCell
                      workOrder={row}
                      finished={finishedJobIds?.has(row.customWorkOrderId)}
                    />
                  </td>
                  <td className="max-w-[240px] truncate px-4 py-3">
                    {row.customerName || "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-semibold tabular-nums">
                    {formatMinutes(row.minutes)}
                  </td>
                  <td className="px-4 py-3 tabular-nums">{row.punches}</td>
                  <td
                    className="max-w-[220px] truncate px-4 py-3"
                    title={row.workers.join(", ")}
                  >
                    {row.workers.join(", ")}
                  </td>
                  <td className="app-muted whitespace-nowrap px-4 py-3 tabular-nums">
                    {dateTime(row.lastActivity)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRightIcon
                      className="app-muted transition group-hover:translate-x-0.5"
                      fontSize="small"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </SectionCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <WorkerHoursTable rows={summary.byWorker} filenameSuffix={suffix} />
        <CategoryHoursTable rows={summary.byCategory} filenameSuffix={suffix} />
      </div>
    </div>
  );
};

// ---- Work order detail ----

// Everything ever punched on one job — a work order or a custom job (no
// date range — the job is the scope), plus manual punch entry and fixes.
// Custom jobs can also be renamed and marked finished here.
const WorkOrderDetail = ({
  jobKey,
  user,
  workers,
  categories,
  customJobs,
  onCustomJobsChanged,
  onBack,
  onForceClose,
  closingId,
  reloadKey,
}) => {
  const { api, shop } = useShopClock();
  const [summary, setSummary] = useState(null);
  const [punches, setPunches] = useState(null);
  const [error, setError] = useState(null);
  const [dialogPunch, setDialogPunch] = useState(null); // null closed, {} adding
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState(null);
  const [allotting, setAllotting] = useState(false);
  const [allotError, setAllotError] = useState(null);

  const load = () => {
    api.fetchSummary(user?.id, { workOrder: jobKey })
      .then((data) => {
        setSummary(data);
        setError(null);
      })
      .catch((err) => setError(err.message));
    api.fetchPunches(user?.id, { workOrder: jobKey, limit: 500 })
      .then(setPunches)
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    setSummary(null);
    setPunches(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobKey, user?.id, reloadKey]);

  const savePunch = async (fields) => {
    setSaving(true);
    setDialogError(null);
    try {
      if (dialogPunch?._id)
        await api.updatePunch(dialogPunch._id, fields, user?.id);
      else await api.createPunch(fields, user?.id);
      setDialogPunch(null);
      load();
    } catch (err) {
      setDialogError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const custom = jobKey.startsWith("c-");
  const customJob = custom
    ? (customJobs || []).find((job) => String(job._id) === jobKey.slice(2))
    : null;
  // The embedded work order details come from whichever punch is newest.
  const workOrder = punches?.[0]?.workOrder;
  const title = custom
    ? customJob?.name || workOrder?.description || "Custom job"
    : `WO ${jobKey}`;
  const suffix = custom ? `-job-${fileSlug(title)}` : `-wo-${jobKey}`;

  // Time used against the allotted time: closed punches plus anyone still
  // punched in (the summary only totals closed punches).
  const usedMinutes = summary
    ? summary.totals.minutes +
      Math.round(
        (punches || [])
          .filter((punch) => punch.status !== "closed")
          .reduce((sum, punch) => sum + punchMinutes(punch), 0)
      )
    : null;
  const allottedMinutes = summary?.allottedMinutes || null;

  const saveAllotted = async (minutes) => {
    setSaving(true);
    setAllotError(null);
    try {
      await api.setAllottedTime(jobKey, minutes, user?.id);
      setAllotting(false);
      load();
    } catch (err) {
      setAllotError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const customJobAction = async (updates) => {
    setSaving(true);
    setRenameError(null);
    setError(null);
    try {
      await api.updateCustomJob(customJob._id, updates, user?.id);
      setRenaming(false);
      onCustomJobsChanged();
    } catch (err) {
      if (updates.name !== undefined) setRenameError(err.message);
      else setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ ml: -1 }}>
          All Work Orders
        </Button>
      </div>

      {error && errorBanner(error)}

      <div className="app-card flex flex-wrap items-center justify-between gap-4 rounded-2xl border p-5">
        <div className="flex min-w-0 items-center gap-4">
          <IconTile
            icon={custom ? HandymanOutlinedIcon : ReceiptLongOutlinedIcon}
            tone={custom ? "amber" : "teal"}
            size="lg"
          />
          <div className="min-w-0">
            <p className="app-eyebrow">{custom ? "Custom job" : "Work order"}</p>
            <h2
              className={`text-xl font-bold tracking-tight ${
                custom ? "" : "tabular-nums"
              }`}
            >
              {title}
            </h2>
            {custom ? (
              <p className="app-muted mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                {customJob && customJob.active === false && (
                  <CustomJobChip finished />
                )}
                <span>
                  Created on the shop tablet
                  {customJob?.createdByName && ` by ${customJob.createdByName}`}
                  {customJob?.createdAt &&
                    ` · ${new Date(customJob.createdAt).toLocaleDateString()}`}
                </span>
              </p>
            ) : (
              <p className="app-muted mt-0.5 text-sm">
                {workOrder?.description && `${workOrder.description} · `}
                {workOrder?.customerName || "Customer unknown"}
                {workOrder?.customerCode && ` · ${workOrder.customerCode}`}
                {workOrder?.orderNumber && ` · Order ${workOrder.orderNumber}`}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {customJob && (
            <>
              <Button
                startIcon={<EditOutlinedIcon />}
                onClick={() => {
                  setRenameError(null);
                  setRenaming(true);
                }}
              >
                Rename
              </Button>
              <Button
                color={customJob.active ? "success" : "inherit"}
                disabled={saving}
                onClick={() => customJobAction({ active: !customJob.active })}
              >
                {customJob.active ? "Mark Finished" : "Reopen"}
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogPunch({})}
          >
            Add Manual Punch
          </Button>
        </div>
      </div>

      {!summary ? (
        <LoadingState label="Loading work order…" />
      ) : (
        <>
          <section className="app-card rounded-2xl border p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="app-eyebrow">Allotted time</p>
                {allottedMinutes ? (
                  <p className="mt-0.5 text-lg font-semibold tabular-nums">
                    {formatMinutes(usedMinutes)}{" "}
                    <span className="app-muted text-sm font-medium">
                      of {formatMinutes(allottedMinutes)} ·{" "}
                      {allottedRemaining(usedMinutes, allottedMinutes)}
                    </span>
                  </p>
                ) : (
                  <p className="app-muted mt-0.5 text-sm">
                    None set. Set one to track this job against it here and on
                    the shop floor TV.
                  </p>
                )}
              </div>
              <Button
                startIcon={<TimerOutlinedIcon />}
                onClick={() => {
                  setAllotError(null);
                  setAllotting(true);
                }}
              >
                {allottedMinutes ? "Change" : "Set Allotted Time"}
              </Button>
            </div>
            {allottedMinutes && (
              <AllottedBar
                used={usedMinutes}
                allotted={allottedMinutes}
                className="mt-3"
              />
            )}
          </section>

          <JobScheduleCard
            jobKey={jobKey}
            title={title}
            summary={summary}
            workers={workers}
            user={user}
            onChanged={load}
          />

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatTile
              icon={AccessTimeIcon}
              tone="sky"
              value={formatMinutes(summary.totals.minutes)}
              label="Hours Worked"
            />
            <StatTile
              icon={PunchClockOutlinedIcon}
              value={summary.totals.punches}
              label="Punches"
            />
            <StatTile
              icon={GroupsOutlinedIcon}
              value={summary.totals.workers}
              label="Workers"
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <WorkerHoursTable rows={summary.byWorker} filenameSuffix={suffix} />
            <CategoryHoursTable
              rows={summary.byCategory}
              filenameSuffix={suffix}
            />
          </div>
        </>
      )}

      <SectionCard
        title="Punches"
        subtitle="Every punch on this work order, newest first."
        action={
          <CsvButton
            data={punchesToCsv(punches)}
            headers={PUNCH_CSV_HEADERS}
            filename={`${shop.fileSlug}-punches${suffix}.csv`}
          />
        }
      >
        <table className="app-table">
          <HeadRow
            columns={[
              "Worker",
              "Category",
              "Punched In",
              "Punched Out",
              "Duration",
              "",
            ]}
          />
          <tbody>
            <PunchRows
              punches={punches}
              showWorkOrder={false}
              onEdit={setDialogPunch}
              onForceClose={onForceClose}
              closingId={closingId}
              colSpan={6}
            />
          </tbody>
        </table>
      </SectionCard>

      <PunchDialog
        open={dialogPunch !== null}
        punch={dialogPunch?._id ? dialogPunch : null}
        fixedJob={{ key: jobKey, label: title }}
        workers={workers}
        categories={categories}
        customJobs={customJobs}
        onSave={savePunch}
        onClose={() => setDialogPunch(null)}
        saving={saving}
        error={dialogError}
      />

      <AllottedTimeDialog
        open={allotting}
        job={{ allottedMinutes, label: title }}
        usedMinutes={usedMinutes}
        onSave={saveAllotted}
        onClose={() => setAllotting(false)}
        saving={saving}
        error={allotError}
      />

      <CustomJobDialog
        open={renaming}
        job={customJob}
        onSave={(fields) => customJobAction(fields)}
        onClose={() => setRenaming(false)}
        saving={saving}
        error={renameError}
      />
    </div>
  );
};

// ---- Punch log tab ----

const PunchLogTab = ({
  punches,
  error,
  workers,
  categories,
  customJobs,
  finishedJobIds,
  filters,
  setFilters,
  onForceClose,
  closingId,
  user,
  onChanged,
}) => {
  const { api, shop } = useShopClock();
  const [dialogPunch, setDialogPunch] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  const savePunch = async (fields) => {
    setSaving(true);
    setDialogError(null);
    try {
      if (dialogPunch?._id)
        await api.updatePunch(dialogPunch._id, fields, user?.id);
      else await api.createPunch(fields, user?.id);
      setDialogPunch(null);
      onChanged();
    } catch (err) {
      setDialogError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && errorBanner(error)}

      <div className="app-card flex flex-wrap items-center gap-2 rounded-2xl border p-3">
        <FormControl size="small" className="w-full sm:w-44">
          <Select
            value={filters.workerId}
            inputProps={{ "aria-label": "Worker" }}
            onChange={(e) =>
              setFilters((f) => ({ ...f, workerId: e.target.value }))
            }
            displayEmpty
          >
            <MenuItem value="">All Workers</MenuItem>
            {(workers || []).map((worker) => (
              <MenuItem key={worker._id} value={String(worker._id)}>
                {worker.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" className="w-full sm:w-44">
          <Select
            value={filters.categoryId}
            inputProps={{ "aria-label": "Category" }}
            onChange={(e) =>
              setFilters((f) => ({ ...f, categoryId: e.target.value }))
            }
            displayEmpty
          >
            <MenuItem value="">All Categories</MenuItem>
            {(categories || []).map((category) => (
              <MenuItem key={category._id} value={String(category._id)}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" className="w-full sm:w-40">
          <Select
            value={filters.status}
            inputProps={{ "aria-label": "Punch status" }}
            onChange={(e) =>
              setFilters((f) => ({ ...f, status: e.target.value }))
            }
            displayEmpty
          >
            <MenuItem value="">All Punches</MenuItem>
            <MenuItem value="open">Still Punched In</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="WO # or job name"
          inputProps={{ "aria-label": "Work order number or job name" }}
          value={filters.workOrder}
          onChange={(e) =>
            setFilters((f) => ({ ...f, workOrder: e.target.value }))
          }
          className="w-full sm:w-48"
        />
        <div className="w-full sm:ml-auto sm:w-auto">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogPunch({})}
          >
            Add Manual Punch
          </Button>
        </div>
      </div>

      <SectionCard
        title="Punch Log"
        subtitle="Newest first — 500 rows max, narrow the range or filters for older punches."
        action={
          <CsvButton
            data={punchesToCsv(punches)}
            headers={PUNCH_CSV_HEADERS}
            filename={`${shop.fileSlug}-punch-log-${new Date().toISOString().slice(0, 10)}.csv`}
          />
        }
      >
        <table className="app-table">
          <HeadRow
            columns={[
              "Worker",
              "Work Order",
              "Category",
              "Punched In",
              "Punched Out",
              "Duration",
              "",
            ]}
          />
          <tbody>
            <PunchRows
              punches={punches}
              showWorkOrder
              finishedJobIds={finishedJobIds}
              onEdit={setDialogPunch}
              onForceClose={onForceClose}
              closingId={closingId}
              colSpan={7}
            />
          </tbody>
        </table>
      </SectionCard>

      <PunchDialog
        open={dialogPunch !== null}
        punch={dialogPunch?._id ? dialogPunch : null}
        fixedJob={null}
        workers={workers}
        categories={categories}
        customJobs={customJobs}
        onSave={savePunch}
        onClose={() => setDialogPunch(null)}
        saving={saving}
        error={dialogError}
      />
    </div>
  );
};

// ---- Open jobs tab ----

// Every active job (Bistrack's open work orders + unfinished custom jobs),
// all time, with its allotted time — including jobs nobody has worked yet,
// so they can be budgeted before work starts.
const OpenJobsTab = ({ user, reloadKey, onOpenJob }) => {
  const { api, shop } = useShopClock();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dialogJob, setDialogJob] = useState(null);
  const [dueJob, setDueJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  const load = () => {
    api.fetchOpenJobs(user?.id)
      .then((result) => {
        setData(result);
        setError(null);
      })
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    if (!user?.id) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, reloadKey]);

  const save = async (allottedMinutes) => {
    setSaving(true);
    setDialogError(null);
    try {
      await api.setAllottedTime(dialogJob.key, allottedMinutes, user?.id);
      setDialogJob(null);
      load();
    } catch (err) {
      setDialogError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveDueDate = async (jobKey, dueDate) => {
    setSaving(true);
    setDialogError(null);
    try {
      await api.setDueDate(jobKey, dueDate, user?.id);
      setDueJob(null);
      load();
    } catch (err) {
      setDialogError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (error && !data) return errorBanner(error);
  if (!data) return <LoadingState label="Loading open jobs…" />;

  const today = todayDay();
  const jobs = data.jobs;
  const budgeted = jobs.filter((job) => job.allottedMinutes > 0);
  const over = budgeted.filter((job) => job.minutes > job.allottedMinutes);

  return (
    <div className="space-y-6">
      {error && errorBanner(error)}
      {data.source === "recent" && (
        <div className="rounded-xl bg-amber-500/10 px-4 py-3 text-sm font-medium text-amber-800 dark:text-amber-300">
          Couldn't reach Bistrack for the open work order list — showing jobs
          worked in the last 14 days instead.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatTile icon={ReceiptLongOutlinedIcon} value={jobs.length} label="Open Jobs" />
        <StatTile
          icon={TimerOutlinedIcon}
          tone="sky"
          value={budgeted.length}
          label="With Allotted Time"
        />
        <StatTile
          icon={WarningAmberOutlinedIcon}
          tone={over.length ? "rose" : "default"}
          value={over.length}
          label="Over Allotted Time"
        />
      </div>

      <SectionCard
        title="Open Jobs"
        subtitle="Every open work order (the list the shop tablet shows) and unfinished custom job, with its time to date. Set an allotted time and the shop floor TV's Job Hours screen shows progress against it; set a due date and it shows on the Schedule tab and the TV's Deadlines screen. Click a row for the job's punches and schedule."
        action={
          <CsvButton
            data={jobs.map((job) => ({
              workOrder: job.worksOrderNumber ?? "",
              job: job.description,
              customJob: job.custom ? "yes" : "",
              customer: job.customerName,
              minutes: job.minutes,
              hours: csvHours(job.minutes),
              allottedHours: job.allottedMinutes ? csvHours(job.allottedMinutes) : "",
              percent: job.allottedMinutes
                ? Math.floor((job.minutes / job.allottedMinutes) * 100)
                : "",
              workingNow: job.workingNow.join(", "),
              dueDate: job.dueDate || "",
            }))}
            headers={[
              { label: "Work Order", key: "workOrder" },
              { label: "Job", key: "job" },
              { label: "Custom Job", key: "customJob" },
              { label: "Customer", key: "customer" },
              { label: "Minutes", key: "minutes" },
              { label: "Hours", key: "hours" },
              { label: "Allotted Hours", key: "allottedHours" },
              { label: "% Used", key: "percent" },
              { label: "Working Now", key: "workingNow" },
              { label: "Due Date", key: "dueDate" },
            ]}
            filename={`${shop.fileSlug}-open-jobs-${new Date().toISOString().slice(0, 10)}.csv`}
          />
        }
      >
        <table className="app-table">
          <HeadRow
            columns={[
              "Work Order",
              "Customer",
              "Working Now",
              "Total Time",
              "Allotted",
              "Progress",
              "Due",
              "",
            ]}
          />
          <tbody>
            {jobs.length === 0 ? (
              <MessageRow colSpan={8}>No open jobs right now.</MessageRow>
            ) : (
              jobs.map((job) => (
                <tr
                  key={job.key}
                  onClick={() => onOpenJob(job.key)}
                  onKeyDown={(e) => e.key === "Enter" && onOpenJob(job.key)}
                  tabIndex={0}
                  className="cursor-pointer focus-visible:bg-[color:var(--app-hover)]"
                >
                  <td className="max-w-[240px] px-4 py-3">
                    <JobCell workOrder={job} />
                  </td>
                  <td className="max-w-[200px] truncate px-4 py-3">
                    {job.customerName || "—"}
                  </td>
                  <td className="max-w-[180px] px-4 py-3">
                    {job.workingNow.length ? (
                      <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700 dark:text-emerald-300">
                        <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-emerald-500" />
                        <span className="truncate">{job.workingNow.join(", ")}</span>
                      </span>
                    ) : (
                      <span className="app-muted text-xs">
                        {job.lastActivity
                          ? `Last ${dateTime(job.lastActivity)}`
                          : "Not started"}
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-semibold tabular-nums">
                    {formatMinutes(job.minutes)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 tabular-nums">
                    {job.allottedMinutes ? formatMinutes(job.allottedMinutes) : "—"}
                  </td>
                  <td className="min-w-[170px] px-4 py-3">
                    {job.allottedMinutes ? (
                      <>
                        <AllottedBar used={job.minutes} allotted={job.allottedMinutes} />
                        <div className="app-muted mt-0.5 text-xs tabular-nums">
                          {allottedRemaining(job.minutes, job.allottedMinutes)}
                        </div>
                      </>
                    ) : (
                      <span className="app-muted">—</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <DueChip dueDate={job.dueDate} today={today} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    <Button
                      size="small"
                      startIcon={<TimerOutlinedIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDialogError(null);
                        setDialogJob(job);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {job.allottedMinutes ? "Change" : "Set Time"}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EventOutlinedIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDialogError(null);
                        setDueJob(job);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      Due
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </SectionCard>

      <AllottedTimeDialog
        open={dialogJob !== null}
        job={
          dialogJob && {
            allottedMinutes: dialogJob.allottedMinutes,
            label: jobText(dialogJob),
          }
        }
        usedMinutes={dialogJob?.minutes}
        onSave={save}
        onClose={() => setDialogJob(null)}
        saving={saving}
        error={dialogError}
      />

      <DueDateDialog
        open={dueJob !== null}
        job={dueJob && { ...dueJob, label: jobText(dueJob) }}
        onSave={saveDueDate}
        onClose={() => setDueJob(null)}
        saving={saving}
        error={dialogError}
      />
    </div>
  );
};

// ---- Custom jobs tab ----

const CustomJobDialog = ({ open, job, onSave, onClose, saving, error }) => {
  const [name, setName] = useState("");

  useEffect(() => {
    if (open) setName(job?.name || "");
  }, [open, job]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Rename Custom Job</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-2">
          {error && errorBanner(error)}
          <TextField
            fullWidth
            size="small"
            label="Name"
            inputProps={{ maxLength: 120 }}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <p className="app-muted text-xs">
            The new name also replaces the old one on this job's past punches.
          </p>
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || saving}
          onClick={() => onSave({ name: name.trim() })}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Jobs workers create on the shop tablet for work with no Bistrack work
// order. Created there, managed here: rename, and mark finished (which takes
// the job off the shop floor board's Job Hours screen).
const CustomJobsTab = ({ customJobs, error, onRefresh, onOpenJob, user }) => {
  const { api } = useShopClock();
  const [dialogJob, setDialogJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [rowError, setRowError] = useState(null);

  const rename = async (fields) => {
    setSaving(true);
    setDialogError(null);
    try {
      await api.updateCustomJob(dialogJob._id, fields, user?.id);
      setDialogJob(null);
      onRefresh();
    } catch (err) {
      setDialogError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (job, active) => {
    setRowError(null);
    try {
      await api.updateCustomJob(job._id, { active }, user?.id);
      onRefresh();
    } catch (err) {
      setRowError(err.message);
    }
  };

  // Active jobs first, then finished; the server already sorts by name.
  const rows = customJobs
    ? [...customJobs].sort((a, b) => Number(b.active) - Number(a.active))
    : null;

  return (
    <div className="space-y-6">
      {(error || rowError) && errorBanner(error || rowError)}

      <SectionCard
        title="Custom Jobs"
        subtitle="Jobs workers create on the shop tablet for work with no Bistrack work order. Mark one finished when the work is done — it comes off the shop floor board's Job Hours screen. Hours are all time."
      >
        <table className="app-table">
          <HeadRow
            columns={["Job", "Hours", "Punches", "Created By", "Status", ""]}
          />
          <tbody>
            {rows === null ? (
              <LoadingRow colSpan={6}>Loading custom jobs...</LoadingRow>
            ) : rows.length === 0 ? (
              <MessageRow colSpan={6}>
                No custom jobs yet — workers create them on the shop tablet
                when a job isn't in the work order list.
              </MessageRow>
            ) : (
              rows.map((job) => (
                <tr key={job._id}>
                  <td className="max-w-[260px] px-4 py-3">
                    <button
                      type="button"
                      onClick={() => onOpenJob(`c-${job._id}`)}
                      className="app-link block max-w-full truncate text-left font-medium"
                      title="Open this job's punches"
                    >
                      {job.name}
                    </button>
                    {job.lastActivity && (
                      <div className="app-muted text-xs">
                        Last punch {dateTime(job.lastActivity)}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-semibold tabular-nums">
                    {formatMinutes(job.minutes)}
                  </td>
                  <td className="px-4 py-3 tabular-nums">{job.punches}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {job.createdByName || "—"}
                    {job.createdAt && (
                      <div className="app-muted text-xs">
                        {new Date(job.createdAt).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <ActiveBadge active={job.active} inactiveLabel="Finished" />
                    {job.workingNow > 0 && (
                      <div className="mt-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                        {job.workingNow} punched in now
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    <Button
                      size="small"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => {
                        setDialogError(null);
                        setDialogJob(job);
                      }}
                    >
                      Rename
                    </Button>
                    <Button
                      size="small"
                      color={job.active ? "success" : "inherit"}
                      onClick={() => setActive(job, !job.active)}
                    >
                      {job.active ? "Mark Finished" : "Reopen"}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </SectionCard>

      <CustomJobDialog
        open={dialogJob !== null}
        job={dialogJob}
        onSave={rename}
        onClose={() => setDialogJob(null)}
        saving={saving}
        error={dialogError}
      />
    </div>
  );
};

// ---- Workers tab ----

const WorkerDialog = ({ open, worker, onSave, onClose, saving, error }) => {
  const { shop } = useShopClock();
  const [name, setName] = useState("");
  const [passcode, setPasscode] = useState("");
  const [personNumber, setPersonNumber] = useState("");

  useEffect(() => {
    if (open) {
      setName(worker?.name || "");
      setPasscode(worker?.passcode || "");
      setPersonNumber(worker?.personNumber || "");
    }
  }, [open, worker]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{worker ? "Edit Worker" : "Add Worker"}</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-2">
          {error && errorBanner(error)}
          <TextField
            fullWidth
            size="small"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Passcode"
            helperText="The code they type on the shop tablet to punch in."
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
          />
          {shop.payrollCheck && (
            <TextField
              fullWidth
              size="small"
              label="UKG # (optional)"
              helperText="Their UKG person number, e.g. 002677 — not their tablet passcode. After 7 PM, if they're still punched into a job but have clocked out in UKG, the job is ended at the time they left."
              value={personNumber}
              onChange={(e) => setPersonNumber(e.target.value)}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || !passcode.trim() || saving}
          onClick={() =>
            onSave({
              name: name.trim(),
              passcode: passcode.trim(),
              ...(shop.payrollCheck ? { personNumber: personNumber.trim() } : {}),
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WorkersTab = ({ workers, error, onRefresh, user }) => {
  const { shop, api } = useShopClock();
  const columns = [
    "Name",
    "Passcode",
    ...(shop.payrollCheck ? ["UKG #"] : []),
    "Face ID",
    "Status",
    "Added",
    "",
  ];
  const [dialogWorker, setDialogWorker] = useState(null); // null closed, {} adding
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [rowError, setRowError] = useState(null);

  const save = async (fields) => {
    setSaving(true);
    setDialogError(null);
    try {
      if (dialogWorker?._id)
        await api.updateWorker(dialogWorker._id, fields, user?.id);
      else await api.createWorker(fields, user?.id);
      setDialogWorker(null);
      onRefresh();
    } catch (err) {
      setDialogError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const rowUpdate = async (worker, updates, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setRowError(null);
    try {
      await api.updateWorker(worker._id, updates, user?.id);
      onRefresh();
    } catch (err) {
      setRowError(err.message);
    }
  };

  return (
    <div className="space-y-6">
      {(error || rowError) && errorBanner(error || rowError)}

      <SectionCard
        title="Workers"
        subtitle={`Deactivate instead of deleting — punch history stays intact. Passcodes must be unique among active workers.${
          shop.payrollCheck
            ? " Add each worker's UKG # (it isn't their passcode) so a job they leave punched into ends when they clock out in UKG."
            : ""
        }`}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogWorker({})}
          >
            Add Worker
          </Button>
        }
      >
        <table className="app-table">
          <HeadRow columns={columns} />
          <tbody>
            {workers === null ? (
              <LoadingRow colSpan={columns.length}>Loading workers...</LoadingRow>
            ) : workers.length === 0 ? (
              <MessageRow colSpan={columns.length}>
                No workers yet — click Add Worker to create the first one.
              </MessageRow>
            ) : (
              workers.map((worker) => (
                <tr key={worker._id}>
                  <td className="px-4 py-3 font-medium">{worker.name}</td>
                  <td className="px-4 py-3 font-mono tabular-nums">{worker.passcode}</td>
                  {shop.payrollCheck && (
                    <td className="whitespace-nowrap px-4 py-3 font-mono tabular-nums">
                      {worker.personNumber || (
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setDialogWorker(worker)}
                        >
                          Add
                        </Button>
                      )}
                    </td>
                  )}
                  <td className="whitespace-nowrap px-4 py-3">
                    {worker.faceId ? (
                      <span className="inline-flex items-center gap-2">
                        Enrolled
                        <button
                          type="button"
                          title="Reset Face ID so they can re-enroll on the app"
                          aria-label={`Reset ${worker.name}'s Face ID`}
                          className="app-muted inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400"
                          onClick={() =>
                            rowUpdate(
                              worker,
                              { resetFace: true },
                              `Reset ${worker.name}'s Face ID? They'll re-enroll next time they punch in on the app.`
                            )
                          }
                        >
                          <FaceRetouchingOffIcon sx={{ fontSize: 16 }} />
                        </button>
                      </span>
                    ) : (
                      <span className="app-muted">Not enrolled</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <ActiveBadge active={worker.active} />
                  </td>
                  <td className="app-muted whitespace-nowrap px-4 py-3 tabular-nums">
                    {worker.createdAt
                      ? new Date(worker.createdAt).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    <Button
                      size="small"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => setDialogWorker(worker)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color={worker.active ? "error" : "success"}
                      onClick={() =>
                        rowUpdate(
                          worker,
                          { active: !worker.active },
                          worker.active
                            ? `Deactivate ${worker.name}? They won't be able to punch in until reactivated.`
                            : null
                        )
                      }
                    >
                      {worker.active ? "Deactivate" : "Activate"}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </SectionCard>

      <WorkerDialog
        open={dialogWorker !== null}
        worker={dialogWorker?._id ? dialogWorker : null}
        onSave={save}
        onClose={() => setDialogWorker(null)}
        saving={saving}
        error={dialogError}
      />
    </div>
  );
};

// ---- Categories tab ----

const CategoryDialog = ({ open, category, onSave, onClose, saving, error }) => {
  const [name, setName] = useState("");

  useEffect(() => {
    if (open) setName(category?.name || "");
  }, [open, category]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{category ? "Rename Category" : "Add Category"}</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-2">
          {error && errorBanner(error)}
          <TextField
            fullWidth
            size="small"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {category && (
            <p className="app-muted text-xs">
              Renaming only affects future punches — past punches keep the name
              they were punched under.
            </p>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || saving}
          onClick={() => onSave({ name: name.trim() })}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CategoriesTab = ({ categories, error, onRefresh, user }) => {
  const { api } = useShopClock();
  const [dialogCategory, setDialogCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [rowError, setRowError] = useState(null);

  const save = async (fields) => {
    setSaving(true);
    setDialogError(null);
    try {
      if (dialogCategory?._id)
        await api.updateCategory(dialogCategory._id, fields, user?.id);
      else await api.createCategory(fields, user?.id);
      setDialogCategory(null);
      onRefresh();
    } catch (err) {
      setDialogError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const rowUpdate = async (category, updates) => {
    setRowError(null);
    try {
      await api.updateCategory(category._id, updates, user?.id);
      onRefresh();
    } catch (err) {
      setRowError(err.message);
    }
  };

  // Swap sortOrder with the neighbor — the order the punch dialog lists them.
  const move = async (index, direction) => {
    const neighbor = categories[index + direction];
    const category = categories[index];
    if (!neighbor) return;
    setRowError(null);
    try {
      await api.updateCategory(
        category._id,
        { sortOrder: neighbor.sortOrder },
        user?.id
      );
      await api.updateCategory(
        neighbor._id,
        { sortOrder: category.sortOrder },
        user?.id
      );
      onRefresh();
    } catch (err) {
      setRowError(err.message);
    }
  };

  return (
    <div className="space-y-6">
      {(error || rowError) && errorBanner(error || rowError)}

      <SectionCard
        title="Categories"
        subtitle="The shop tablet no longer asks for a category — new punches come in as None. Categories here can still be set on a punch with Fix or Add Manual Punch, and older punches keep theirs. Deactivate instead of deleting — punch history stays intact."
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogCategory({})}
          >
            Add Category
          </Button>
        }
      >
        <table className="app-table">
          <HeadRow columns={["Order", "Name", "Status", ""]} />
          <tbody>
            {categories === null ? (
              <LoadingRow colSpan={4}>Loading categories...</LoadingRow>
            ) : categories.length === 0 ? (
              <MessageRow colSpan={4}>
                No categories yet — click Add Category to create the first one.
              </MessageRow>
            ) : (
              categories.map((category, index) => (
                <tr key={category._id}>
                  <td className="whitespace-nowrap px-4 py-2">
                    <span className="inline-flex items-center gap-0.5">
                      <button
                        type="button"
                        disabled={index === 0}
                        aria-label={`Move ${category.name} up`}
                        title="Move up"
                        className="app-muted inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors enabled:hover:bg-[color:var(--app-hover)] enabled:hover:text-[color:var(--app-text)] disabled:opacity-30"
                        onClick={() => move(index, -1)}
                      >
                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                      </button>
                      <button
                        type="button"
                        disabled={index === categories.length - 1}
                        aria-label={`Move ${category.name} down`}
                        title="Move down"
                        className="app-muted inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors enabled:hover:bg-[color:var(--app-hover)] enabled:hover:text-[color:var(--app-text)] disabled:opacity-30"
                        onClick={() => move(index, 1)}
                      >
                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                      </button>
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">{category.name}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <ActiveBadge active={category.active} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    <Button
                      size="small"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => setDialogCategory(category)}
                    >
                      Rename
                    </Button>
                    <Button
                      size="small"
                      color={category.active ? "error" : "success"}
                      onClick={() =>
                        rowUpdate(category, { active: !category.active })
                      }
                    >
                      {category.active ? "Deactivate" : "Activate"}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </SectionCard>

      <CategoryDialog
        open={dialogCategory !== null}
        category={dialogCategory?._id ? dialogCategory : null}
        onSave={save}
        onClose={() => setDialogCategory(null)}
        saving={saving}
        error={dialogError}
      />
    </div>
  );
};

// ---- Dashboard ----

// One dashboard per shop clock (shopClocks.js): /pcw and /mill-shop.
const ShopClockDashboard = ({ shopKey }) => {
  const shop = SHOP_CLOCKS[shopKey];
  return (
    <ShopClockProvider value={shop}>
      <ShopClockDashboardBody />
    </ShopClockProvider>
  );
};

const ShopClockDashboardBody = () => {
  const { api, shop } = useShopClock();
  const user = useSelector(selectUser);
  const role = useSelector(selectRoles);
  const canView = shop.roles.some((r) => role?.roles?.includes(r));

  // The Selects, text fields, and dialogs are MUI, which keeps its own
  // palette — feed the in-app toggle into a MUI theme so their text stays
  // legible on the dark cards.
  const muiTheme = useAppMuiTheme();

  // ?view= deep-links a tab; ?wo= deep-links one job's breakdown — a work
  // order number or "c-<id>" for a custom job.
  const [searchParams, setSearchParams] = useSearchParams();
  const paramView = searchParams.get("view");
  const activeTab = TABS.some((tab) => tab.key === paramView)
    ? paramView
    : "hours";
  const workOrderParam = searchParams.get("wo");
  const openWorkOrder = isJobKey(workOrderParam) ? workOrderParam : null;

  const setParams = (view, wo) => {
    const params = {};
    if (view && view !== "hours") params.view = view;
    if (wo) params.wo = String(wo);
    setSearchParams(params, { replace: true });
  };
  const selectTab = (key) => setParams(key, null);

  // Date range: presets or custom from/to; custom wins when either is set.
  const [rangeKey, setRangeKey] = useState("7d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const range = useMemo(() => {
    if (customFrom || customTo) {
      return {
        from: customFrom
          ? new Date(`${customFrom}T00:00:00`).toISOString()
          : undefined,
        to: customTo
          ? new Date(`${customTo}T23:59:59.999`).toISOString()
          : undefined,
      };
    }
    const preset = RANGES.find((r) => r.key === rangeKey);
    if (!preset?.days) return {};
    return { from: startOfDaysAgo(preset.days).toISOString() };
  }, [rangeKey, customFrom, customTo]);

  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [punches, setPunches] = useState(null);
  const [punchesError, setPunchesError] = useState(null);
  const [punchFilters, setPunchFilters] = useState({
    workerId: "",
    categoryId: "",
    status: "",
    workOrder: "",
  });
  const [workers, setWorkers] = useState(null);
  const [workersError, setWorkersError] = useState(null);
  const [categories, setCategories] = useState(null);
  const [categoriesError, setCategoriesError] = useState(null);
  const [customJobs, setCustomJobs] = useState(null);
  const [customJobsError, setCustomJobsError] = useState(null);
  const [closingId, setClosingId] = useState(null);
  // Bumped after any punch change so the WO detail view refetches too.
  const [reloadKey, setReloadKey] = useState(0);

  const loadSummary = () => {
    api.fetchSummary(user?.id, range)
      .then((data) => {
        setSummary(data);
        setSummaryError(null);
      })
      .catch((err) => setSummaryError(err.message));
  };

  const loadPunches = () => {
    api.fetchPunches(user?.id, { ...range, ...punchFilters })
      .then((rows) => {
        setPunches(rows);
        setPunchesError(null);
      })
      .catch((err) => setPunchesError(err.message));
  };

  const loadWorkers = () => {
    api.fetchWorkers(user?.id)
      .then((rows) => {
        setWorkers(rows);
        setWorkersError(null);
      })
      .catch((err) => setWorkersError(err.message));
  };

  const loadCategories = () => {
    api.fetchCategories(user?.id)
      .then((rows) => {
        setCategories(rows);
        setCategoriesError(null);
      })
      .catch((err) => setCategoriesError(err.message));
  };

  const loadCustomJobs = () => {
    api.fetchCustomJobs(user?.id)
      .then((rows) => {
        setCustomJobs(rows);
        setCustomJobsError(null);
      })
      .catch((err) => setCustomJobsError(err.message));
  };

  // A rename rewrites the job's punches, so everything showing it reloads.
  const customJobsChanged = () => {
    loadCustomJobs();
    loadSummary();
    loadPunches();
    setReloadKey((key) => key + 1);
  };

  const finishedJobIds = useMemo(
    () =>
      new Set(
        (customJobs || [])
          .filter((job) => !job.active)
          .map((job) => String(job._id))
      ),
    [customJobs]
  );

  const punchesChanged = () => {
    loadSummary();
    loadPunches();
    loadCustomJobs();
    setReloadKey((key) => key + 1);
  };

  useEffect(() => {
    if (!canView || !user?.id) return;
    setSummary(null);
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, user?.id, range]);

  useEffect(() => {
    if (!canView || !user?.id) return;
    setPunches(null);
    loadPunches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, user?.id, range, punchFilters]);

  // Workers, categories, and custom jobs double as filter and punch-dialog
  // options, so load them up front.
  useEffect(() => {
    if (!canView || !user?.id) return;
    loadWorkers();
    loadCategories();
    loadCustomJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, user?.id]);

  const forceClose = async (punch) => {
    if (
      !window.confirm(
        `Force punch out ${punch.workerName} from ${jobText(
          punch.workOrder
        )}? The punch closes at the current time.`
      )
    )
      return;
    setClosingId(punch._id);
    try {
      await api.closePunch(punch._id, user?.id);
      punchesChanged();
    } catch (err) {
      setSummaryError(err.message);
      setPunchesError(err.message);
    } finally {
      setClosingId(null);
    }
  };

  if (!canView) {
    return (
      <PageShell size="sm">
        <EmptyState
          icon={PunchClockOutlinedIcon}
          title={shop.title}
          description={`You need an ${shop.roles.join(" or ")} role to access this dashboard.`}
        />
      </PageShell>
    );
  }

  const showRange =
    !openWorkOrder && (activeTab === "hours" || activeTab === "punches");

  return (
    <ThemeProvider theme={muiTheme}>
      <PageShell size="2xl">
        <PageHeader
          icon={PunchClockOutlinedIcon}
          tone="teal"
          eyebrow="Admin"
          eyebrowTo="/admin"
          title={shop.title}
          description={`${shop.place}: punch in/out time from the shop floor — hours by work order (including custom jobs created on the tablet), worker, and category, allotted time and due dates per job, the schedule of who is on which job, plus custom job, worker, and category management. Punching itself happens on the shop's tablet in the mobile app.`}
        />

        <div className="space-y-6">
          {openWorkOrder ? (
            <WorkOrderDetail
              jobKey={openWorkOrder}
              user={user}
              workers={workers}
              categories={categories}
              customJobs={customJobs}
              onCustomJobsChanged={customJobsChanged}
              onBack={() => setParams(activeTab, null)}
              onForceClose={forceClose}
              closingId={closingId}
              reloadKey={reloadKey}
            />
          ) : (
            <>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <nav
                  role="tablist"
                  aria-label={`${shop.title} sections`}
                  className={SEGMENT_TRACK}
                >
                  {TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.key}
                      onClick={() => selectTab(tab.key)}
                      className={segmentClass(activeTab === tab.key, "md")}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>

                {showRange && (
                  <div className="flex flex-wrap items-center gap-2">
                    <div
                      role="group"
                      aria-label="Date range"
                      className={SEGMENT_TRACK}
                    >
                      {RANGES.map((preset) => {
                        const active =
                          rangeKey === preset.key && !customFrom && !customTo;
                        return (
                          <button
                            key={preset.key}
                            type="button"
                            aria-pressed={active}
                            onClick={() => {
                              setRangeKey(preset.key);
                              setCustomFrom("");
                              setCustomTo("");
                            }}
                            className={segmentClass(active)}
                          >
                            {preset.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <input
                        type="date"
                        aria-label="From date"
                        className={`${DATE_INPUT} ${
                          customFrom ? "!border-sky-500" : ""
                        }`}
                        value={customFrom}
                        onChange={(e) => setCustomFrom(e.target.value)}
                      />
                      <span className="app-muted text-sm">to</span>
                      <input
                        type="date"
                        aria-label="To date"
                        className={`${DATE_INPUT} ${
                          customTo ? "!border-sky-500" : ""
                        }`}
                        value={customTo}
                        onChange={(e) => setCustomTo(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>

              {activeTab === "hours" && (
                <HoursTab
                  summary={summary}
                  error={summaryError}
                  onForceClose={forceClose}
                  closingId={closingId}
                  onOpenWorkOrder={(wo) => setParams(activeTab, wo)}
                  finishedJobIds={finishedJobIds}
                />
              )}
              {activeTab === "open" && (
                <OpenJobsTab
                  user={user}
                  reloadKey={reloadKey}
                  onOpenJob={(key) => setParams(activeTab, key)}
                />
              )}
              {activeTab === "schedule" && (
                <ScheduleTab
                  user={user}
                  workers={workers}
                  reloadKey={reloadKey}
                  onOpenJob={(key) => setParams(activeTab, key)}
                />
              )}
              {activeTab === "punches" && (
                <PunchLogTab
                  punches={punches}
                  error={punchesError}
                  workers={workers}
                  categories={categories}
                  customJobs={customJobs}
                  finishedJobIds={finishedJobIds}
                  filters={punchFilters}
                  setFilters={setPunchFilters}
                  onForceClose={forceClose}
                  closingId={closingId}
                  user={user}
                  onChanged={punchesChanged}
                />
              )}
              {activeTab === "jobs" && (
                <CustomJobsTab
                  customJobs={customJobs}
                  error={customJobsError}
                  onRefresh={customJobsChanged}
                  onOpenJob={(key) => setParams(activeTab, key)}
                  user={user}
                />
              )}
              {activeTab === "workers" && (
                <WorkersTab
                  workers={workers}
                  error={workersError}
                  onRefresh={loadWorkers}
                  user={user}
                />
              )}
              {activeTab === "categories" && (
                <CategoriesTab
                  categories={categories}
                  error={categoriesError}
                  onRefresh={loadCategories}
                  user={user}
                />
              )}
            </>
          )}
        </div>
      </PageShell>
    </ThemeProvider>
  );
};

const PCWDashboard = () => <ShopClockDashboard shopKey="pcw" />;
export const MillShopDashboard = () => <ShopClockDashboard shopKey="mill" />;

export default PCWDashboard;
